import * as fs from 'node:fs';
import * as os from 'node:os';
import * as path from 'node:path';
import { spawn } from 'node:child_process';
import {
  ByteBudget,
  readBoundedRegularFile,
  replaceFileAtomically,
} from './byte-budget';
import { sleepMs, truncate } from './utils';

const CAPACITY_MESSAGES = new Set([
  'Selected model is at capacity. Please try a different model.',
  'ERROR: Selected model is at capacity. Please try a different model.',
]);

const CODEX_PREFIX_DIR = '.codex-review-codex';
const CODEX_HOME_DIR = '.codex-review-auth';
const STDERR_TAIL_MAX_LINES = 20;
const STDERR_TAIL_MAX_CHARS = 4000;
const EVENT_LINE_MAX_CHARS = 4 * 1024 * 1024;
const FAILURE_DIAGNOSTIC_MAX_CHARS = 3000;

interface RunCodexOptions {
  label: string;
  workspace: string;
  prompt: string;
  schemaFile: string;
  outputFile: string;
  outputDir: string;
  tempDir: string;
  diagnosticBudget: ByteBudget;
  maxReviewOutputBytes: number;
  attemptPrefix: string;
  model: string;
  sandbox: string;
  safePath: string;
  maxAttempts: number;
  initialBackoffSeconds: number;
  info: (message: string) => void;
  log: (message: string) => void;
}

export interface RunCodexResult {
  status: number;
  attempt: number;
  durationMs: number;
  eventsFile: string;
  stderrFile: string;
  eventCount: number;
  commandCount: number;
  usageSummary: string | null;
  stderrTail: string | null;
  failureDiagnostic: string | null;
  diagnosticsTruncated: boolean;
}

export async function runCodexWithRetries(options: RunCodexOptions): Promise<RunCodexResult> {
  let lastResult: RunCodexResult | null = null;
  for (let attempt = 1; attempt <= options.maxAttempts; attempt += 1) {
    const result = await runCodexAttempt(options, attempt);
    lastResult = result;
    if (result.status === 0) {
      return result;
    }
    if (!result.retryableFailure) {
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} exited with code ${result.status}; failure was not retryable, failing closed`);
      break;
    }
    if (attempt < options.maxAttempts) {
      const backoffMs = options.initialBackoffSeconds * 1000 * (1 << (attempt - 1));
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} hit a retryable transient failure; retrying in ${Math.ceil(backoffMs / 1000)}s`);
      await sleepMs(backoffMs);
    } else {
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} hit a retryable transient failure; no attempts remain`);
    }
  }
  if (!lastResult) {
    throw new Error(`${options.label} did not run any Codex attempts.`);
  }
  return lastResult;
}

async function runCodexAttempt(options: RunCodexOptions, attempt: number): Promise<RunCodexResult & { retryableFailure: boolean }> {
  fs.mkdirSync(options.outputDir, { recursive: true });
  const attemptBase = path.join(options.outputDir, `${options.attemptPrefix}-attempt-${attempt}`);
  const eventsFile = `${attemptBase}.events.jsonl`;
  const stderrFile = `${attemptBase}.stderr.log`;
  const home = process.env.HOME;
  if (!home) {
    throw new Error('Codex Review requires HOME to resolve the self-hosted Codex account paths.');
  }
  const attemptTempDir = fs.mkdtempSync(path.join(options.tempDir, 'codex-attempt-'));
  const attemptOutputFile = path.join(attemptTempDir, 'review-output.json');
  const codexBin = path.join(home, CODEX_PREFIX_DIR, 'bin', 'codex');
  let lineBuffer = '';
  let stderrTailBuffer = '';
  let sawStderr = false;
  const stats = newCodexEventStats();
  const startedAt = Date.now();
  options.info(`${options.label} attempt ${attempt}/${options.maxAttempts}: starting Codex.`);
  let eventsCapture: CappedDiagnosticFile | null = null;
  let stderrCapture: CappedDiagnosticFile | null = null;
  let primaryError: unknown = null;
  try {
    fs.chmodSync(attemptTempDir, 0o700);
    const events = new CappedDiagnosticFile(eventsFile, options.diagnosticBudget);
    eventsCapture = events;
    const stderr = new CappedDiagnosticFile(stderrFile, options.diagnosticBudget);
    stderrCapture = stderr;
    const status = await executeCodex(codexBin, [
      'exec',
      '--ephemeral',
      '--json',
      '--cd',
      options.workspace,
      '--model',
      options.model,
      '--sandbox',
      options.sandbox,
      '--output-schema',
      options.schemaFile,
      '--output-last-message',
      attemptOutputFile,
      '-',
    ], {
      input: Buffer.from(options.prompt, 'utf8'),
      env: codexEnv(options, attemptTempDir),
      stdout: (data: Buffer) => {
        events.write(data);
        lineBuffer = processCodexEventChunk(lineBuffer + data.toString('utf8'), stats);
      },
      stderr: (data: Buffer) => {
        sawStderr = true;
        stderr.write(data);
        stderrTailBuffer = appendTail(stderrTailBuffer, data.toString('utf8'), STDERR_TAIL_MAX_CHARS * 2);
      },
    });
    processCodexEventRemainder(lineBuffer, stats);
    if (status === 0) {
      stageBoundedReviewOutput({
        sourceFile: attemptOutputFile,
        outputFile: options.outputFile,
        maxBytes: options.maxReviewOutputBytes,
      });
    }

    const stderrTail = tailText(stderrTailBuffer, STDERR_TAIL_MAX_LINES, STDERR_TAIL_MAX_CHARS);
    const failureDiagnostic = selectFailureDiagnostic(stats, stderrTail);
    if (status !== 0 && failureDiagnostic !== null) {
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} failure diagnostic:\n${failureDiagnostic}`);
    } else if (status !== 0) {
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} emitted no fatal diagnostic message.`);
    } else if (sawStderr) {
      options.info(`${options.label} attempt ${attempt}/${options.maxAttempts} wrote stderr diagnostics to ${stderrFile}.`);
    }
    const diagnosticsTruncated = events.truncated || stderr.truncated;
    if (diagnosticsTruncated) {
      options.log(`${options.label} attempt ${attempt}/${options.maxAttempts} diagnostics were truncated after the action-wide ${options.diagnosticBudget.limitBytes}-byte budget was exhausted.`);
    }
    const durationMs = Date.now() - startedAt;
    const usage = stats.usageSummary ? `; ${stats.usageSummary}` : '';
    options.info(`${options.label} attempt ${attempt}/${options.maxAttempts}: exited ${status} in ${formatDuration(durationMs)}; captured ${stats.eventCount} event(s), ${stats.commandCount} command event(s)${usage}. Diagnostics: ${eventsFile}`);
    return {
      status,
      attempt,
      durationMs,
      eventsFile,
      stderrFile,
      eventCount: stats.eventCount,
      commandCount: stats.commandCount,
      usageSummary: stats.usageSummary,
      stderrTail,
      failureDiagnostic,
      diagnosticsTruncated,
      retryableFailure: isRetryableCodexStats(stats),
    };
  } catch (error) {
    primaryError = error;
    throw error;
  } finally {
    const cleanupFailures: string[] = [];
    for (const capture of [eventsCapture, stderrCapture]) {
      try {
        capture?.close();
      } catch (error) {
        cleanupFailures.push(error instanceof Error ? error.message : String(error));
      }
    }
    try {
      fs.rmSync(attemptTempDir, { recursive: true, force: true, maxRetries: 3, retryDelay: 200 });
    } catch (error) {
      cleanupFailures.push(error instanceof Error ? error.message : String(error));
    }
    if (cleanupFailures.length > 0) {
      const message = `Could not fully clean Codex attempt storage: ${cleanupFailures.join('; ')}`;
      if (primaryError === null) {
        throw new Error(message);
      }
      options.log(message);
    }
  }
}

function stageBoundedReviewOutput({
  sourceFile,
  outputFile,
  maxBytes,
}: {
  sourceFile: string;
  outputFile: string;
  maxBytes: number;
}): void {
  const content = readBoundedRegularFile({
    file: sourceFile,
    maxBytes,
    description: 'Review output',
    limitName: 'max-review-output-bytes',
  });

  replaceFileAtomically(outputFile, content);
}

async function executeCodex(
  command: string,
  args: string[],
  {
    input,
    env,
    stdout,
    stderr,
  }: {
    input: Buffer;
    env: NodeJS.ProcessEnv;
    stdout: (data: Buffer) => void;
    stderr: (data: Buffer) => void;
  },
): Promise<number> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      env,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    let processError: Error | null = null;

    const noteProcessError = (error: unknown): void => {
      if (processError !== null) {
        return;
      }
      processError = error instanceof Error ? error : new Error(String(error));
      if (child.pid) {
        child.kill('SIGTERM');
      }
    };

    child.stdout.on('data', (data: Buffer) => {
      try {
        stdout(data);
      } catch (error) {
        noteProcessError(error);
      }
    });
    child.stderr.on('data', (data: Buffer) => {
      try {
        stderr(data);
      } catch (error) {
        noteProcessError(error);
      }
    });
    child.on('error', noteProcessError);
    child.stdin.on('error', (error: NodeJS.ErrnoException) => {
      if (error.code !== 'EPIPE') {
        noteProcessError(error);
      }
    });
    child.on('close', (code, signal) => {
      if (processError !== null) {
        reject(processError);
      } else if (code === null) {
        reject(new Error(`Codex process exited without a status code${signal ? ` (signal ${signal})` : ''}.`));
      } else {
        resolve(code);
      }
    });
    child.stdin.end(input);
  });
}

interface CodexEventStats {
  eventCount: number;
  commandCount: number;
  usageSummary: string | null;
  sawReviewOutput: boolean;
  sawCapacityError: boolean;
  malformedEvent: boolean;
  topLevelFailureMessage: string | null;
  turnFailureMessage: string | null;
}

function newCodexEventStats(): CodexEventStats {
  return {
    eventCount: 0,
    commandCount: 0,
    usageSummary: null,
    sawReviewOutput: false,
    sawCapacityError: false,
    malformedEvent: false,
    topLevelFailureMessage: null,
    turnFailureMessage: null,
  };
}

function processCodexEventChunk(text: string, stats: CodexEventStats): string {
  const lines = text.split(/\r?\n/);
  const remainder = lines.pop() || '';
  for (const line of lines) {
    noteCodexEvent(line, stats);
  }
  if (remainder.length > EVENT_LINE_MAX_CHARS) {
    stats.malformedEvent = true;
    return '';
  }
  return remainder;
}

function processCodexEventRemainder(text: string, stats: CodexEventStats): void {
  if (text.trim() !== '') {
    noteCodexEvent(text, stats);
  }
}

function noteCodexEvent(line: string, stats: CodexEventStats): void {
  if (!line.trim()) {
    return;
  }
  stats.eventCount += 1;
  let event: {
    type?: string;
    message?: string;
    error?: { message?: string };
    item?: { type?: string; message?: string };
    usage?: unknown;
  };
  try {
    event = JSON.parse(line);
  } catch {
    stats.malformedEvent = true;
    return;
  }
  noteCapacityMessage(event.message, stats);
  noteCapacityMessage(event.error?.message, stats);
  // Item-level errors can be non-fatal warnings. Only protocol-level failure
  // events are eligible to explain a nonzero Codex exit in user-facing output.
  if (event.type === 'error') {
    stats.topLevelFailureMessage = cleanFailureDiagnostic(event.message)
      || stats.topLevelFailureMessage;
  }
  if (event.type === 'turn.failed') {
    stats.turnFailureMessage = cleanFailureDiagnostic(event.error?.message)
      || stats.turnFailureMessage;
  }
  if (event.item?.type === 'command_execution') {
    stats.commandCount += 1;
  }
  if (event.type === 'item.started') {
    const itemType = event.item?.type;
    if (itemType && itemType !== 'error') {
      stats.sawReviewOutput = true;
    }
  }
  if (event.type === 'item.completed') {
    noteCapacityMessage(event.item?.message, stats);
    if (event.item?.type && event.item.type !== 'error') {
      stats.sawReviewOutput = true;
    }
  }
  if (event.type === 'turn.completed') {
    stats.sawReviewOutput = true;
  }
  const usage = summarizeUsage(event.usage);
  if (usage) {
    stats.usageSummary = usage;
  }
}

function summarizeUsage(value: unknown): string | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const usage = value as Record<string, unknown>;
  const input = readNumber(usage.input_tokens) ?? readNumber(usage.inputTokens);
  const output = readNumber(usage.output_tokens) ?? readNumber(usage.outputTokens);
  const total = readNumber(usage.total_tokens) ?? readNumber(usage.totalTokens);
  const parts = [
    input === null ? null : `${input} input token(s)`,
    output === null ? null : `${output} output token(s)`,
    total === null ? null : `${total} total token(s)`,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(', ') : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function tailText(value: string, maxLines: number, maxChars: number): string | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const lines = trimmed.split(/\r?\n/);
  const lineTail = lines.slice(Math.max(0, lines.length - maxLines)).join('\n');
  return lineTail.length > maxChars ? lineTail.slice(lineTail.length - maxChars) : lineTail;
}

function appendTail(current: string, chunk: string, maxChars: number): string {
  const combined = current + chunk;
  return combined.length > maxChars ? combined.slice(combined.length - maxChars) : combined;
}

class CappedDiagnosticFile {
  private descriptor: number | null;
  truncated = false;

  constructor(
    filename: string,
    private readonly budget: ByteBudget,
  ) {
    try {
      fs.unlinkSync(filename);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== 'ENOENT') {
        throw error;
      }
    }
    this.descriptor = fs.openSync(
      filename,
      fs.constants.O_WRONLY
        | fs.constants.O_CREAT
        | fs.constants.O_EXCL
        | fs.constants.O_NOFOLLOW,
      0o600,
    );
  }

  write(data: Buffer): void {
    if (this.descriptor === null) {
      throw new Error('Cannot write to a closed diagnostic file.');
    }
    const bytesToWrite = this.budget.reserveAvailable(data.length);
    if (bytesToWrite < data.length) {
      this.truncated = true;
    }
    let offset = 0;
    while (offset < bytesToWrite) {
      offset += fs.writeSync(this.descriptor, data, offset, bytesToWrite - offset);
    }
  }

  close(): void {
    if (this.descriptor !== null) {
      fs.closeSync(this.descriptor);
      this.descriptor = null;
    }
  }
}

export function formatDuration(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

function codexEnv(options: RunCodexOptions, attemptTempDir: string): { [key: string]: string } {
  const home = process.env.HOME;
  if (!home) {
    throw new Error('Codex Review requires HOME to resolve the self-hosted Codex account paths.');
  }
  const env: { [key: string]: string } = {
    HOME: home,
    USER: process.env.USER || os.userInfo().username,
    SHELL: process.env.SHELL || '/bin/bash',
    PATH: options.safePath,
    TMPDIR: attemptTempDir,
    TMP: attemptTempDir,
    TEMP: attemptTempDir,
    GOTMPDIR: attemptTempDir,
    LANG: 'C.UTF-8',
    LC_ALL: 'C.UTF-8',
    CODEX_HOME: path.join(home, CODEX_HOME_DIR),
  };
  if (process.env.RUNNER_TRACKING_ID) {
    env.RUNNER_TRACKING_ID = process.env.RUNNER_TRACKING_ID;
  }
  return env;
}

export function isRetryableCodexFailure(eventsText: string): boolean {
  const stats = newCodexEventStats();
  const remainder = processCodexEventChunk(eventsText, stats);
  processCodexEventRemainder(remainder, stats);
  return isRetryableCodexStats(stats);
}

function noteCapacityMessage(message: unknown, stats: CodexEventStats): void {
  if (typeof message === 'string' && CAPACITY_MESSAGES.has(message.trim())) {
    stats.sawCapacityError = true;
  }
}

function isRetryableCodexStats(stats: CodexEventStats): boolean {
  return stats.sawCapacityError && !stats.sawReviewOutput && !stats.malformedEvent;
}

function selectFailureDiagnostic(
  stats: CodexEventStats,
  stderrTail: string | null,
): string | null {
  return stats.turnFailureMessage
    || stats.topLevelFailureMessage
    || cleanFailureDiagnostic(stderrTail);
}

function cleanFailureDiagnostic(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const cleaned = value
    .replace(/\u001B\][^\u0007]*(?:\u0007|\u001B\\)/g, '')
    .replace(/\u001B\[[0-?]*[ -/]*[@-~]/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
    .trim();
  return cleaned === '' ? null : truncate(cleaned, FAILURE_DIAGNOSTIC_MAX_CHARS);
}
